import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, Pause, Play, Volume2, VolumeX, Music, Heart } from "lucide-react";
import { canSpeak, speak, stopSpeaking } from "@/lib/readAloud";
import { getVoicePref } from "@/lib/voicePref";
import { isSoundEnabled } from "@/lib/chime";
import {
  startHealingTone432, stopHealingTone432, duckHealingTone432,
} from "@/lib/healingTone432";
import { scaleToTarget, totalMs, speakMs } from "@/lib/meditationVariants";

/**
 * LovingKindnessMeditation — a 10-minute Metta / loving-kindness
 * meditation delivered as a beat sequence of `speak` and `pause` steps.
 *
 * Follows the traditional Metta arc — self → someone dear → someone
 * neutral → all beings — with the four wishes ("May I be safe / healthy
 * / peaceful / loved") in each phase, and a soft closing.
 *
 * The pad underneath is a continuous 432 Hz healing tone (not the
 * standard breath ambience) — the whole practice sits inside a warm
 * musical field that gently breathes on its own.
 *
 * Pause lengths follow the script exactly. Speech beats wait for the
 * TTS utterance to finish before advancing so pacing is real, never
 * estimated.
 */

const BEATS = [
  // ------- Opening / settling -------
  { type: "speak", text: "Begin by settling into a comfortable position. Let your body relax. Let your breath soften. Allow the gentle 432 hertz healing tones to fill the space around you." },
  { type: "pause", ms: 10000 },
  { type: "speak", text: "Close your eyes. Feel the weight of your body. Feel the support beneath you. Let your breath move naturally." },
  { type: "pause", ms: 15000 },
  { type: "speak", text: "Take a slow inhale… and a slow exhale." },
  { type: "pause", ms: 10000 },
  { type: "speak", text: "Let your breath settle into a calm rhythm." },

  // ------- Phase 1 — self -------
  { type: "phase", label: "Loving-kindness toward yourself" },
  { type: "speak", text: "Bring your awareness to your heart. Feel the gentle warmth there. Feel the soft pulse of your breath." },
  { type: "pause", ms: 15000 },
  { type: "speak", text: "Silently repeat: May I be safe." },
  { type: "pause", ms: 10000 },
  { type: "speak", text: "May I be healthy." },
  { type: "pause", ms: 10000 },
  { type: "speak", text: "May I be peaceful." },
  { type: "pause", ms: 10000 },
  { type: "speak", text: "May I be loved." },
  { type: "pause", ms: 15000 },
  { type: "speak", text: "Let these words settle into your body. Let them soften your chest. Let them calm your mind." },
  { type: "pause", ms: 20000 },

  // ------- Phase 2 — someone you care about -------
  { type: "phase", label: "Loving-kindness toward someone you care about" },
  { type: "speak", text: "Bring to mind someone you care for deeply. Someone who brings warmth to your heart." },
  { type: "pause", ms: 15000 },
  { type: "speak", text: "See them clearly. Feel your connection to them." },
  { type: "pause", ms: 10000 },
  { type: "speak", text: "Silently repeat: May you be safe." },
  { type: "pause", ms: 10000 },
  { type: "speak", text: "May you be healthy." },
  { type: "pause", ms: 10000 },
  { type: "speak", text: "May you be peaceful." },
  { type: "pause", ms: 10000 },
  { type: "speak", text: "May you be loved." },
  { type: "pause", ms: 15000 },
  { type: "speak", text: "Let the feeling of kindness expand toward them. Warm… gentle… steady." },
  { type: "pause", ms: 20000 },

  // ------- Phase 3 — someone neutral -------
  { type: "phase", label: "Loving-kindness toward someone neutral" },
  { type: "speak", text: "Now bring to mind someone neutral. Someone you don't know well, but who shares this world with you." },
  { type: "pause", ms: 15000 },
  { type: "speak", text: "Hold them gently in your awareness." },
  { type: "pause", ms: 10000 },
  { type: "speak", text: "Silently repeat: May you be safe." },
  { type: "pause", ms: 10000 },
  { type: "speak", text: "May you be healthy." },
  { type: "pause", ms: 10000 },
  { type: "speak", text: "May you be peaceful." },
  { type: "pause", ms: 10000 },
  { type: "speak", text: "May you be loved." },
  { type: "pause", ms: 15000 },
  { type: "speak", text: "Feel your heart expanding beyond familiarity. Beyond preference. Into simple human connection." },
  { type: "pause", ms: 20000 },

  // ------- Phase 4 — all beings -------
  { type: "phase", label: "Loving-kindness toward all beings" },
  { type: "speak", text: "Now imagine your heart opening wider. Wider than your body. Wider than this room. Wider than this moment." },
  { type: "pause", ms: 20000 },
  { type: "speak", text: "Let your kindness extend outward — to all people, all beings, everywhere." },
  { type: "pause", ms: 15000 },
  { type: "speak", text: "Silently repeat: May all beings be safe." },
  { type: "pause", ms: 10000 },
  { type: "speak", text: "May all beings be healthy." },
  { type: "pause", ms: 10000 },
  { type: "speak", text: "May all beings be peaceful." },
  { type: "pause", ms: 10000 },
  { type: "speak", text: "May all beings be loved." },
  { type: "pause", ms: 20000 },
  { type: "speak", text: "Feel the warmth of your heart radiating outward. Feel the gentle tones supporting that expansion. Feel your presence softening into compassion." },
  { type: "pause", ms: 20000 },

  // ------- Phase 5 — closing -------
  { type: "phase", label: "Closing" },
  { type: "speak", text: "Bring your awareness back to your breath. Back to your body. Back to this moment." },
  { type: "pause", ms: 15000 },
  { type: "speak", text: "Take one slow inhale… and one full exhale." },
  { type: "pause", ms: 10000 },
  { type: "speak", text: "Feel the calm in your chest. Feel the warmth in your heart. Feel the steadiness in your body." },
  { type: "pause", ms: 15000 },
  { type: "speak", text: "When you're ready, gently open your eyes. Carry this loving-kindness with you." },
];

// Rough per-beat duration used only for the top progress bar.
function beatDurationMs(beat) {
  if (beat.type === "pause") return beat.ms || 0;
  if (beat.type === "phase") return 800; // phase transitions are visual-only, ~1s
  if (beat.type === "speak") return speakMs(beat.text);
  return 0;
}

const TOTAL_MS = BEATS.reduce((acc, b) => acc + beatDurationMs(b), 0);

// Wall-clock targets by variant.
const TARGETS = {
  beginner:      5 * 60 * 1000,
  intermediate: 10 * 60 * 1000,
  advanced:     20 * 60 * 1000,
};

export default function LovingKindnessMeditation({ open, onClose, variant = "intermediate" }) {
  const target = TARGETS[variant] ?? TARGETS.intermediate;
  const beats = React.useMemo(() => scaleToTarget(BEATS, target, variant), [target, variant]);
  const TOTAL_MS_LOCAL = React.useMemo(() => totalMs(beats), [beats]);
  const [beatIdx, setBeatIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [narrating, setNarrating] = useState(() => canSpeak() && getVoicePref().enabled);
  const [music, setMusic] = useState(() => isSoundEnabled());
  const [elapsedMs, setElapsedMs] = useState(0);
  const [currentPhase, setCurrentPhase] = useState("");
  const cancelledRef = useRef(false);
  const timeoutRef = useRef(null);

  // Reset when opened.
  useEffect(() => {
    if (!open) return;
    cancelledRef.current = false;
    setBeatIdx(0);
    setPaused(false);
    setElapsedMs(0);
    setCurrentPhase("");
  }, [open]);

  // Cleanup on unmount.
  useEffect(() => () => {
    cancelledRef.current = true;
    stopSpeaking();
    stopHealingTone432();
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  }, []);

  // Music lifecycle — the 432 Hz pad.
  useEffect(() => {
    if (!open) { stopHealingTone432(); return undefined; }
    if (!music) { stopHealingTone432(); return undefined; }
    startHealingTone432();
    return () => { /* handled by open==false branch */ };
  }, [open, music]);

  useEffect(() => {
    if (!music || !open) return;
    duckHealingTone432(paused ? 0.35 : 1.0, 0.6);
  }, [paused, music, open]);

  // Beat sequencer.
  useEffect(() => {
    if (!open || paused || cancelledRef.current) return undefined;
    const beat = beats[beatIdx];
    if (!beat) return undefined;
    let cleanup = () => {};

    const advance = () => {
      if (cancelledRef.current) return;
      setElapsedMs((ms) => ms + beatDurationMs(beat));
      if (beatIdx + 1 >= beats.length) return;
      setBeatIdx(beatIdx + 1);
    };

    if (beat.type === "speak") {
      if (narrating && canSpeak()) {
        speak(beat.text, { context: "meditation", onEnd: advance });
        cleanup = () => stopSpeaking();
      } else {
        timeoutRef.current = setTimeout(advance, beatDurationMs(beat));
        cleanup = () => timeoutRef.current && clearTimeout(timeoutRef.current);
      }
    } else if (beat.type === "pause") {
      timeoutRef.current = setTimeout(advance, beat.ms || 0);
      cleanup = () => timeoutRef.current && clearTimeout(timeoutRef.current);
    } else if (beat.type === "phase") {
      setCurrentPhase(beat.label);
      timeoutRef.current = setTimeout(advance, 800);
      cleanup = () => timeoutRef.current && clearTimeout(timeoutRef.current);
    }

    return () => { cleanup(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [beatIdx, open, paused, narrating]);

  useEffect(() => {
    if (paused) {
      stopSpeaking();
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    }
  }, [paused]);

  useEffect(() => {
    const onPref = () => {
      const enabled = getVoicePref().enabled;
      if (!enabled) { setNarrating(false); stopSpeaking(); }
    };
    window.addEventListener("n8-voice-pref-changed", onPref);
    return () => window.removeEventListener("n8-voice-pref-changed", onPref);
  }, []);

  if (!open) return null;

  const beat = beats[beatIdx];
  const finished = beatIdx >= beats.length - 1 && !paused;
  const progress = Math.min(100, Math.round((elapsedMs / TOTAL_MS_LOCAL) * 100));
  const displayText = beat?.type === "speak" ? beat.text : "";

  return createPortal(
    <div
      className="fixed inset-0 z-[100] overflow-hidden"
      role="dialog"
      aria-modal="true"
      aria-label="Loving-kindness meditation"
      data-testid="loving-kindness-overlay"
    >
      {/* Deep, warm ambient backdrop */}
      <div
        className="absolute inset-0"
        style={{ backgroundColor: "rgba(24, 14, 12, 0.985)" }}
      />
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at 50% 45%, rgba(230,140,110,0.20) 0%, rgba(90,40,30,0.06) 45%, transparent 78%)",
        }}
      />

      {/* Progress bar */}
      <div className="absolute top-0 left-0 right-0 h-0.5 bg-white/5" data-testid="loving-kindness-progress-track">
        <div
          className="h-full bg-[hsl(var(--terracotta))]/75 transition-[width] duration-700"
          style={{ width: `${progress}%` }}
          data-testid="loving-kindness-progress-bar"
        />
      </div>

      {/* Phase label */}
      {currentPhase && (
        <div
          className="absolute top-4 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-[0.28em] text-white/60"
          data-testid="loving-kindness-phase"
        >
          {currentPhase}
        </div>
      )}

      {/* Close — z-30 so it sits above the main stage's z-10 layer and
          actually receives pointer events. */}
      <button
        type="button"
        onClick={onClose}
        aria-label="Close meditation"
        data-testid="loving-kindness-close"
        className="absolute top-4 right-4 z-30 rounded-full p-2.5 text-white/80 hover:text-white bg-white/5 hover:bg-white/15 tab-link"
      >
        <X size={18} />
      </button>

      {/* Main stage */}
      <div className="relative z-10 h-full w-full flex flex-col items-center justify-center px-8 text-center gap-10">
        <HeartGlow />

        <div className="max-w-2xl min-h-[3.5em]">
          <p
            key={beatIdx}
            className="font-display italic text-white/90 text-2xl md:text-3xl leading-relaxed animate-[fade-up_800ms_ease-out]"
            data-testid="loving-kindness-text"
          >
            {displayText}
          </p>
        </div>

        {/* Bottom controls */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setPaused((p) => !p)}
            aria-label={paused ? "Resume meditation" : "Pause meditation"}
            data-testid="loving-kindness-toggle"
            className="rounded-full p-2 text-white/80 hover:text-white hover:bg-white/10 tab-link"
            disabled={finished}
          >
            {paused ? <Play size={16} /> : <Pause size={16} />}
          </button>
          {canSpeak() && (
            <button
              type="button"
              onClick={() => setNarrating((n) => !n)}
              aria-label={narrating ? "Mute N8's voice" : "Unmute N8's voice"}
              aria-pressed={narrating}
              data-testid="loving-kindness-narration-toggle"
              className={`rounded-full p-2 tab-link ${
                narrating
                  ? "text-white bg-white/15 hover:bg-white/25"
                  : "text-white/60 hover:text-white hover:bg-white/10"
              }`}
              disabled={finished}
            >
              {narrating ? <Volume2 size={15} /> : <VolumeX size={15} />}
            </button>
          )}
          <button
            type="button"
            onClick={() => setMusic((m) => !m)}
            aria-label={music ? "Mute 432 Hz healing tones" : "Play 432 Hz healing tones"}
            aria-pressed={music}
            data-testid="loving-kindness-music-toggle"
            title={music ? "432 Hz tones on" : "432 Hz tones off"}
            className={`rounded-full p-2 tab-link ${
              music
                ? "text-white bg-white/15 hover:bg-white/25"
                : "text-white/60 hover:text-white hover:bg-white/10"
            }`}
            disabled={finished}
          >
            <Music size={15} />
          </button>
        </div>

        {finished && (
          <div className="text-[11px] uppercase tracking-[0.28em] text-white/60" data-testid="loving-kindness-complete">
            Meditation complete
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

/**
 * HeartGlow — a warm, softly-pulsing heart glow visual sized to match
 * the still ring in the other meditations. Radiates gentle amber light,
 * slower than a real heartbeat — this is a metaphor for the heart, not
 * a metronome.
 */
function HeartGlow() {
  return (
    <div className="relative grid place-items-center h-[300px] w-[300px]" data-testid="loving-kindness-glow">
      <div
        aria-hidden
        className="absolute rounded-full"
        style={{
          width: 260,
          height: 260,
          animation: "lk-aura-pulse 6s ease-in-out infinite",
          background: "radial-gradient(circle, rgba(230,140,110,0.30) 0%, rgba(230,140,110,0) 68%)",
        }}
      />
      <div
        aria-hidden
        className="absolute rounded-full border border-[hsl(var(--terracotta))]/45"
        style={{
          width: 200,
          height: 200,
          animation: "lk-ring-pulse 6s ease-in-out infinite",
        }}
      />
      <Heart
        aria-hidden
        size={40}
        strokeWidth={1.4}
        className="text-[hsl(var(--terracotta))] relative z-10"
        style={{ animation: "lk-heart-pulse 6s ease-in-out infinite" }}
      />
      <style>{`
        @keyframes lk-ring-pulse {
          0%, 100% { transform: scale(0.94); opacity: 0.6; }
          50%      { transform: scale(1.08); opacity: 1;   }
        }
        @keyframes lk-aura-pulse {
          0%, 100% { opacity: 0.55; transform: scale(0.95); }
          50%      { opacity: 1;    transform: scale(1.1);  }
        }
        @keyframes lk-heart-pulse {
          0%, 100% { transform: scale(0.94); }
          50%      { transform: scale(1.08); }
        }
      `}</style>
    </div>
  );
}
